import { Component } from 'react'
import axios from 'axios'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import withStyles from 'beam/dist/styles'
import classNames from 'classnames'
import personIcon from '../../Assets/person_icon.svg'
import { Dialog, DialogContent, DialogActions } from 'beam/dist/Dialog'
import Typography from 'beam/dist/Typography'
import Grid from 'beam/dist/Grid'
import Button from 'beam/dist/Button'
import Avatar from 'beam/dist/Avatar'
import { isNull, isUndefined, trim, round } from 'lodash'
import { authenticatedUserShape, withAuthenticatedUser, PERMISSIONS } from '../../Permissions'


const autoQaIdPrefix = 'AgentProfileDialog-'

const styles = theme => {
  const avatarSize = theme.spacing(15)
  const padding = theme.spacing(2)
  const titleWidth = 100
  return {
    loading: {
      justifyContent: 'center',
      alignItems: 'center',
      flexGrow: 1,
      display: 'flex',
      padding: theme.spacing(3),
    },
    muiDialogPaper: {
      maxWidth: 450,
      width: '100%',
    },
    agentInfoContainer: {
      padding: theme.spacing(1, 0, 1, 2),
      flex: `1 0 calc(100% - ${avatarSize}px)`,
    },
    agentDetailItem: {
      display: 'flex',
      width: '100%',
    },
    'agent-detail-title': {
      flex: `0 0 ${titleWidth}px`,
      marginRight: theme.spacing(1),
    },
    'agent-detail-value': {
      flex: '0 1 auto',
    },
    last30DaysItem: {
      '& $agent-detail-title': {
        flex: `0 0 ${avatarSize + padding + titleWidth}px`,
      },
    },
    avatar: {
      width: avatarSize,
      height: avatarSize,
      fontSize: avatarSize / 3,
      zIndex: 1,
      color: theme.palette.grey[800],
      backgroundColor: theme.palette.common.white,
    },
    title: {
      display: 'inline',
    },
    avatarAndInfo: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      padding,
    },
    last30DaysContainer: {
      padding: `${padding}px ${padding}px 0 ${padding}px`,
    },
    dialogContainer: {
      padding: 0,
      '&:first-child': {
        paddingTop: 0,
      },
      maxWidth: 450,
    },
    titleContainer: {
      paddingBottom: theme.spacing(1),
    },
    agentName: {
      textDecoration: 'underline',
    },
    agentNameLink: {
      color: 'inherit',
    },
    openAgentProfileIcon: {
      position: 'relative',
      left: 4,
      top: 3,
    },
  }
}

class AgentProfileDialog extends Component {
  static propTypes = {
    agent: PropTypes.oneOfType([PropTypes.object, PropTypes.shape({
      acdId: PropTypes.number,
      activitySummary: PropTypes.object,
      displayId: PropTypes.string.isRequired,
      group: PropTypes.oneOfType([
        PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
          }),
        ),
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
        }),
      ]),
      id: PropTypes.number.isRequired,
      roles: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
        }),
      ),
      shortDisplayId: PropTypes.string.isRequired,
      skillMappings: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
        }),
      ),
      team: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      }),
    })]),
    classes: PropTypes.exact({
      'agent-detail-title': PropTypes.string.isRequired,
      'agent-detail-value': PropTypes.string.isRequired,
      agentDetailItem: PropTypes.string.isRequired,
      agentInfoContainer: PropTypes.string.isRequired,
      agentName: PropTypes.string.isRequired,
      agentNameLink: PropTypes.string.isRequired,
      avatar: PropTypes.string.isRequired,
      avatarAndInfo: PropTypes.string.isRequired,
      dialogContainer: PropTypes.string.isRequired,
      last30DaysContainer: PropTypes.string.isRequired,
      last30DaysItem: PropTypes.string.isRequired,
      loading: PropTypes.string.isRequired,
      muiDialogPaper: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      titleContainer: PropTypes.string.isRequired,
      openAgentProfileIcon: PropTypes.string.isRequired,
    }).isRequired,
    open: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    t: PropTypes.func.isRequired,
    theme: PropTypes.object,
    authenticatedUser: authenticatedUserShape.isRequired,
  }

  static defaultProps = {
    onOpen: () => {},
    onClose: () => {},
    error: null,
  }

  source = axios.CancelToken.source()
  componentMounted = true

  componentWillUnmount() {
    this.componentMounted = false
    this.source.cancel()
  }

  handleClose = () => {
    const { onClose } = this.props
    onClose()
  }

  handleDialogOpened = () => {
    const { onOpen } = this.props
    onOpen(this.source.token)
  }


  getAgentName = agent => agent.displayId

  getAgentInitials = agent => agent.shortDisplayId

  getAgentPictureUrl = agent =>
    agent.picture
      ? agent.picture
      : (initials => (initials ? null : trim(personIcon, '"')))(this.getAgentInitials(agent))

  renderDetailItem = (key, val, { variant = 'caption', isSplit = true, addedClasses = [] } = {}) => {
    const { classes } = this.props
    return (
      <div
        className={classNames([classes.agentDetailItem, ...addedClasses])}
        data-testid={`${autoQaIdPrefix}dialog-detailItem-${key}`}
      >
        <Typography className={classes['agent-detail-title']} variant={variant} color="inherit">{`${key}${
          !isSplit ? ':' : ''
        } `}</Typography>
        <Typography className={classes['agent-detail-value']} variant={variant} color="inherit">
          {val}
        </Typography>
      </div>
    )
  }

  /* Renders the link the the Agent's profile or, if the link is unavailable, renders the Agent Name without the link */
  renderAgentProfileLink = () => {
    const { classes, agent } = this.props
    return (
      <Typography
        color="inherit"
        variant="subtitle1"
        className={classes.agentName}
        data-testid={`${autoQaIdPrefix}dialog-agentName`}
      >
        {this.getAgentName(agent)}
      </Typography>
    )
  }

  renderContent = () => {
    const {
      classes,
      t,
      agent,
      authenticatedUser: { hasPermission },
    } = this.props
    const hasAnalytics = hasPermission(PERMISSIONS.VIEW_CAA_IN_RECORDING_APP)

    return (
      <div>
        <Grid container alignItems="center" className={classes.avatarAndInfo} wrap="nowrap">
          <div>
            <Avatar className={classes.avatar} alt={this.getAgentName(agent)} src={this.getAgentPictureUrl(agent)}>
              {this.getAgentInitials(agent)}
            </Avatar>
          </div>
          <div className={classes.agentInfoContainer}>
            <div className={classes.titleContainer}>
              {this.renderAgentProfileLink()}
              {agent &&
                agent.roles &&
                agent.roles.map(({ name }) => (
                  <Typography key={name} color="inherit" variant="subtitle2">
                    {name}
                  </Typography>
                ))}
            </div>
            {agent && agent.acdId && this.renderDetailItem(t('acdId'), agent.acdId.toString(10), { isSplit: false })}
            {agent && agent.group && this.renderDetailItem(t('group'), agent.group.name, { isSplit: false })}
            {agent && agent.team && this.renderDetailItem(t('team'), agent.team.name, { isSplit: false })}
            {agent &&
              agent.skillMappings &&
              Boolean(agent.skillMappings.length) &&
              this.renderDetailItem(
                t('skillMapping'),
                agent.skillMappings.reduce((skillStr, { name }) => `${skillStr}, ${name}`, '').substring(2),
                { isSplit: false },
              )}
          </div>
        </Grid>
        <div className={classes.last30DaysContainer}>
          <div className={classes.titleContainer}>
            <Typography variant="subtitle2" className={classes.title}>
              {`${t('scores')} `}
            </Typography>
            <Typography variant="caption" className={classes.title}>
              {`(${t('last30Days')})`}
            </Typography>
          </div>
          {(!agent.activitySummary || !agent.activitySummary['30day']) && (
            <Typography variant="subtitle2">{t('surveyScore_none')}</Typography>
          )}
          {hasAnalytics &&
            agent.activitySummary &&
            agent.activitySummary['30day'] &&
            !isUndefined(agent.activitySummary['30day'].netReputation) &&
            this.renderDetailItem(
              t('netRepScore'),
              isNull(agent.activitySummary['30day'].netReputation) || !agent.activitySummary['30day'].contactCount
                ? t('surveyScore_none')
                : round(agent.activitySummary['30day'].netReputation, 2).toString(10),
              { addedClasses: [classes.last30DaysItem] },
            )}
          {hasAnalytics &&
            agent.activitySummary &&
            agent.activitySummary['30day'] &&
            !isUndefined(agent.activitySummary['30day'].avgPredictiveNPS) &&
            this.renderDetailItem(
              t('avgPredictiveNps'),
              isNull(agent.activitySummary['30day'].avgPredictiveNPS) || !agent.activitySummary['30day'].contactCount
                ? t('surveyScore_none')
                : round(agent.activitySummary['30day'].avgPredictiveNPS, 2).toString(10),
              { addedClasses: [classes.last30DaysItem] },
            )}
          {agent.activitySummary &&
            agent.activitySummary['30day'] &&
            !isUndefined(agent.activitySummary['30day'].contactCount) &&
            this.renderDetailItem(
              t('completedContacts'),
              isNull(agent.activitySummary['30day'].contactCount)
                ? t('surveyScore_none')
                : agent.activitySummary['30day'].contactCount.toString(10),
              { addedClasses: [classes.last30DaysItem] },
            )}
        </div>
      </div>
    )
  }

  render = () => {
    const { open, loading, error, t, classes } = this.props
    return (
      <Dialog
        open={open}
        onClose={this.handleClose}
        onEnter={this.handleDialogOpened}
        disableEnforceFocus={true}
        classes={{ paper: classes.muiDialogPaper }}
        auto-qa-id={`${autoQaIdPrefix}dialog`}
        data-testid={`${autoQaIdPrefix}dialog`}
      >
        <DialogContent className={classes.dialogContainer}>
          {!loading && !error && open && this.renderContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleClose} data-testid={`${autoQaIdPrefix}dialog-closeButton`}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
}

export { AgentProfileDialog as UnstyledAgentProfileDialog }

export default withTranslation()(withStyles(styles, { withTheme: true })(withAuthenticatedUser(AgentProfileDialog)))
